// K/L. Integrity checks, the append-only compliance audit trail, exports,
// and who holds compliance access.
import { useCallback, useEffect, useState } from "react";
import { Download, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import {
  CAP, can, downloadExport, fmtDateTime, humanise, legalError, plGet, plPatch, plPost,
} from "../../lib/platformLegal";
import {
  Button, Empty, Field, Loading, Modal, Note, Section, Stat, Table, TonePill, inputClass,
} from "./ui";

export default function AuditSection({ capabilities }) {
  const [integrity, setIntegrity] = useState(null);
  const [audit, setAudit] = useState(null);
  const [exports, setExports] = useState([]);
  const [roles, setRoles] = useState(null);
  const [granting, setGranting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const jobs = [plGet("/integrity"), plGet("/audit", { params: { limit: 100 } })];
      const [integrityResult, auditResult] = await Promise.all(jobs);
      setIntegrity(integrityResult);
      setAudit(auditResult);
      if (can(capabilities, CAP.export)) {
        setExports((await plGet("/exports")).exports);
      }
      if (can(capabilities, CAP.roles)) {
        setRoles(await plGet("/roles"));
      }
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, [capabilities]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading label="Verifying the compliance trail..." />;

  const chain = integrity?.audit_chain;
  return (
    <div className="space-y-4">
      <Section testId="pl-integrity" title="Integrity"
               description="Everything that can be checked mechanically, checked.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <IntegrityCard ok={chain?.ok} title="Audit hash chain"
                         detail={chain?.ok
                           ? `${chain.checked} entries verified end to end.`
                           : `Chain break at ${chain?.broken_at} (${chain?.reason}).`}
                         testId="pl-integrity-chain" />
          <IntegrityCard ok={integrity?.legal_documents?.ok} title="Legal document hashes"
                         detail={integrity?.legal_documents?.ok
                           ? `${integrity.legal_documents.checked} versions match their hash.`
                           : `${integrity?.legal_documents?.mismatched?.length} version(s) do not match.`}
                         testId="pl-integrity-documents" />
          <IntegrityCard ok={integrity?.storage?.durable} title="Document storage"
                         detail={integrity?.storage?.note || integrity?.storage?.provider}
                         testId="pl-integrity-storage" />
          <IntegrityCard ok={integrity?.privileged_mfa?.status === "verified"}
                         title="Privileged MFA"
                         detail={integrity?.privileged_mfa?.detail}
                         testId="pl-integrity-mfa" />
        </div>

        {integrity?.session_security && (
          <div className="mt-3">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Authentication hardening
              ({integrity.session_security.satisfied}/{integrity.session_security.total})
            </div>
            <ul className="mt-2 space-y-1">
              {integrity.session_security.checks.map((check) => (
                <li key={check.name} className="flex flex-wrap items-start gap-2 text-xs">
                  <TonePill tone={check.status === "verified"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-900"}>
                    {check.name}
                  </TonePill>
                  <span className="text-slate-600 flex-1 min-w-[12rem]">{check.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Note tone="slate" testId="pl-integrity-note">
          The chain makes tampering evident, not impossible: a row removed or edited directly in
          the database breaks verification and is detectable here. There is no update or delete
          path to this collection anywhere in the codebase.
        </Note>
      </Section>

      {exports.length > 0 && (
        <Section testId="pl-exports" title="Exports"
                 description="Sensitive identifiers leave masked, evidence leaves as a reference
                              rather than content, and every export is audited.">
          <div className="flex flex-wrap gap-2">
            {exports.map((item) => (
              <Button key={item.key} variant="secondary"
                      onClick={() => downloadExport(item.key).catch(
                        (err) => toast.error(legalError(err)))}
                      data-testid={`pl-export-${item.key}`}>
                <Download className="w-4 h-4" /> {humanise(item.key)}
              </Button>
            ))}
          </div>
        </Section>
      )}

      {roles && (
        <Section testId="pl-roles" title="Compliance access"
                 description="A business account can never hold one of these, whatever else it is.
                              Handling a government demand is granted by name, never by job title."
                 actions={<Button onClick={() => setGranting(true)} data-testid="pl-grant-role">
                   Grant access
                 </Button>}>
          {roles.roles.length === 0 ? (
            <Empty testId="pl-roles-empty">Nobody holds a compliance role yet.</Empty>
          ) : (
            <Table head={["Person", "Role", "Extra capabilities", "Status", ""]}
                   testId="pl-roles-table" minWidth={660}>
              {roles.roles.map((row) => (
                <tr key={row.id} data-testid={`pl-role-${row.id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-slate-900">{row.user_name || "—"}</div>
                    <div className="text-xs text-slate-500 break-all">{row.user_email}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs">{humanise(row.role)}</td>
                  <td className="py-2 pr-3 text-xs">
                    {(row.extra_capabilities || []).join(", ") || "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs">{humanise(row.status)}</td>
                  <td className="py-2 pr-3">
                    {row.status === "active" && (
                      <Button variant="secondary" onClick={async () => {
                        const reason = window.prompt("Why is this access being revoked?");
                        if (!reason) return;
                        try {
                          await plPatch(`/roles/${row.id}`, { status: "revoked", reason });
                          toast.success("Access revoked.");
                          load();
                        } catch (err) {
                          toast.error(legalError(err));
                        }
                      }}>Revoke</Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Section>
      )}

      <Section testId="pl-audit" title="Compliance audit trail"
               description={`${audit?.total ?? 0} entries. Append-only: there is no edit or delete
                             path, and each row is chained to the one before it.`}>
        {!audit?.entries?.length ? (
          <Empty testId="pl-audit-empty">Nothing recorded yet.</Empty>
        ) : (
          <Table head={["#", "When", "Action", "Entity", "Who", "Reason"]}
                 testId="pl-audit-table" minWidth={760}>
            {audit.entries.map((entry) => (
              <tr key={entry.id} data-testid={`pl-audit-${entry.sequence}`}>
                <td className="py-2 pr-3 text-xs tabular-nums text-slate-500">{entry.sequence}</td>
                <td className="py-2 pr-3 text-xs">{fmtDateTime(entry.created_at)}</td>
                <td className="py-2 pr-3 text-xs">{humanise(entry.action)}</td>
                <td className="py-2 pr-3 text-xs">
                  {humanise(entry.entity_type)}
                  <div className="text-slate-500 break-all">{entry.entity_id}</div>
                </td>
                <td className="py-2 pr-3 text-xs">{entry.actor_name}</td>
                <td className="py-2 pr-3 text-xs text-slate-600">{entry.reason || "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {granting && roles && (
        <GrantModal roles={roles} onClose={() => setGranting(false)}
                    onSaved={() => { setGranting(false); load(); }} />
      )}
    </div>
  );
}

function IntegrityCard({ ok, title, detail, testId }) {
  const Icon = ok ? ShieldCheck : ShieldAlert;
  return (
    <div className={`rounded-xl border p-3 ${ok
      ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
         data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${ok ? "text-emerald-700" : "text-amber-700"}`} />
        <span className="text-sm font-medium text-slate-900">{title}</span>
      </div>
      <p className="mt-1 text-xs text-slate-700">{detail}</p>
    </div>
  );
}

function GrantModal({ roles, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_email: "", role: roles.available[0], extra_capabilities: [], reason: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await plPost("/roles", form);
      toast.success("Compliance access granted.");
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Grant compliance access" onClose={onClose} testId="pl-grant-modal">
      <Field label="Platform account email"
             hint="A user with a business account cannot hold a compliance role.">
        <input className={inputClass} value={form.user_email}
               onChange={(e) => setForm({ ...form, user_email: e.target.value })}
               data-testid="pl-grant-email" />
      </Field>
      <Field label="Role">
        <select className={inputClass} value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="pl-grant-role-select">
          {roles.available.map((role) => (
            <option key={role} value={role}>{humanise(role)}</option>
          ))}
        </select>
      </Field>
      <Field label="Additional capabilities"
             hint="Handling government demands is never bundled with a role.">
        <div className="flex flex-wrap gap-1.5">
          {roles.explicit_only.map((capability) => {
            const on = form.extra_capabilities.includes(capability);
            return (
              <button key={capability} type="button"
                      onClick={() => setForm({
                        ...form,
                        extra_capabilities: on
                          ? form.extra_capabilities.filter((c) => c !== capability)
                          : [...form.extra_capabilities, capability],
                      })}
                      className={`rounded-full border px-2 py-0.5 text-xs ${on
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-700"}`}>
                {capability}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Reason">
        <input className={inputClass} value={form.reason}
               onChange={(e) => setForm({ ...form, reason: e.target.value })}
               data-testid="pl-grant-reason" />
      </Field>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} busy={busy} data-testid="pl-grant-save">Grant</Button>
      </div>
    </Modal>
  );
}
